/**
 * Input/output contract for the `duplicateEvent` callable.
 *
 * Duplicates an event "minus attendees": the event doc (details,
 * branding, sessionTypes, FAQs), its eventLocations, its sessions
 * (+ sessionInternals), and its custom groups (named non-system
 * audiences, copied with EMPTY individual membership — roles kept).
 * Never copied: eventParticipants, invitations, emailDeliveries,
 * notificationReceipts, surveyResponses, sessionNotes, sponsors,
 * travelItems, surveys, notifications, and the three non-group audience
 * kinds (role audiences are re-bootstrapped for the new event, personal
 * audiences self-heal when people are invited, anonymous compositions
 * are recreated per referencing session).
 *
 * The whole schedule shifts by the calendar-day delta between the
 * source event's startDate and `newStartDate`, preserving each
 * session's local wall-clock time in the event's IANA timezone
 * (DST-safe). endDate keeps the source's duration in days.
 *
 * This must be a callable (not a client write): Firestore rules block
 * clients from creating named audiences, and the multi-collection copy
 * plus audience/location ID remapping needs the admin SDK.
 *
 * Authorization: global admin, or an organizer who is an active
 * participant of the SOURCE event.
 */

import { z } from 'zod';

export const duplicateEventInputSchema = z
  .object({
    sourceEventId: z.string().min(1),
    /** Name for the copy. Must not collide (case-insensitive). */
    name: z.string().min(1).max(200),
    /** YYYY-MM-DD, event-local. The copy's first day. */
    newStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  })
  .strict();

export type DuplicateEventInput = z.infer<typeof duplicateEventInputSchema>;

export interface DuplicateEventResponse {
  success: true;
  newEventId: string;
  counts: {
    sessions: number;
    sessionInternals: number;
    locations: number;
    /** Named custom groups copied (with empty individual membership). */
    groups: number;
    /** Anonymous compositions recreated for referencing sessions. */
    anonymousAudiences: number;
  };
}
